import {
  EventOperationsState,
  PublicOfferDocument,
  Sponsor,
} from '../types';

const MAX_SLUG_LENGTH = 64;
const RESERVED_PUBLIC_SLUGS = new Set([
  'admin', 'app', 'cases', 'flow', 'hospitality', 'nosotros', 'portal', 'recursos', 'soluciones',
]);

export function normalizePublicOfferSlug(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, MAX_SLUG_LENGTH)
    .replace(/-+$/g, '');
}

export function createPublicOfferSlug(name: string): string {
  return normalizePublicOfferSlug(name) || 'mi-oferta';
}

export function isValidPublicOfferSlug(slug: string): boolean {
  return /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/.test(slug) && !RESERVED_PUBLIC_SLUGS.has(slug);
}

export function getPublicOfferUrl(slug: string): string {
  const configuredOrigin = String(import.meta.env.VITE_PUBLIC_SITE_URL || '').replace(/\/$/, '');
  const browserOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const isLocal = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const origin = configuredOrigin || (isLocal ? browserOrigin : 'https://grosym.site');
  return `${origin}/${normalizePublicOfferSlug(slug)}`;
}

export function buildPublicOfferDocument(
  state: EventOperationsState,
  sponsors: Sponsor[],
  organizationId: string,
  projectId: string,
  slugInput: string,
  publishedAt = new Date().toISOString(),
): PublicOfferDocument {
  if (!state.landingPage) throw new Error('PUBLIC_OFFER_LANDING_REQUIRED');
  const slug = normalizePublicOfferSlug(slugInput);
  if (!isValidPublicOfferSlug(slug)) throw new Error('PUBLIC_OFFER_INVALID_SLUG');

  const selectedSponsorIds = new Set(state.eventSponsorIds ?? []);
  const publicLanding = {
    ...state.landingPage,
    publishingChoice: 'grosym' as const,
    status: 'published' as const,
    slug,
    publishedAt,
    updatedAt: publishedAt,
  };

  return {
    version: 1,
    slug,
    status: 'published',
    organizationId,
    projectId,
    eventId: state.event.id,
    event: { ...state.event, updatedAt: publishedAt },
    landingPage: publicLanding,
    ticketTypes: state.ticketTypes
      .filter((ticket) => ticket.active && ticket.visibility === 'public')
      .map(({ accessCode: _accessCode, nextTicketTypeId: _nextTicketTypeId, ...ticket }) => ({
        ...ticket,
        visibility: 'public' as const,
      })),
    products: (state.products ?? []).filter((product) => product.active).map((product) => ({ ...product })),
    speakers: state.speakers
      .filter((speaker) => speaker.status !== 'cancelled')
      .map((speaker) => ({
        id: speaker.id,
        eventId: speaker.eventId,
        firstName: speaker.firstName,
        lastName: speaker.lastName,
        ...(speaker.photoUrl ? { photoUrl: speaker.photoUrl } : {}),
        ...(speaker.bio ? { bio: speaker.bio } : {}),
        role: speaker.role,
        institution: speaker.institution,
        country: speaker.country,
        specialty: speaker.specialty,
        ...(speaker.linkedinUrl ? { linkedinUrl: speaker.linkedinUrl } : {}),
        ...(speaker.instagramUrl ? { instagramUrl: speaker.instagramUrl } : {}),
        status: speaker.status,
      })),
    sessions: state.sessions
      .filter((session) => session.status !== 'cancelled')
      .map((session) => ({ ...session, assetUrls: [] })),
    rooms: state.rooms.map((room) => ({ ...room })),
    registrationFields: state.registrationFields
      .filter((field) => field.active !== false)
      .map((field) => ({ ...field })),
    ...(state.paymentConfiguration ? {
      paymentConfiguration: {
        acceptsPayments: state.paymentConfiguration.acceptsPayments,
        method: state.paymentConfiguration.method,
        ...(state.paymentConfiguration.methods ? { methods: [...state.paymentConfiguration.methods] } : {}),
        connectionStatus: state.paymentConfiguration.connectionStatus,
        updatedAt: publishedAt,
      },
    } : {}),
    partners: [...(state.partners ?? state.acquisitionPlan?.allies ?? [])],
    sponsors: sponsors
      .filter((sponsor) => selectedSponsorIds.has(sponsor.id))
      .map((sponsor) => ({
        id: sponsor.id,
        companyName: sponsor.companyName,
        ...(sponsor.websiteUrl ? { websiteUrl: sponsor.websiteUrl } : {}),
        ...(sponsor.logoUrl ? { logoUrl: sponsor.logoUrl } : {}),
        tier: sponsor.tier,
      })),
    publishedAt,
    updatedAt: publishedAt,
  };
}

export function publicOfferToPreviewState(offer: PublicOfferDocument): EventOperationsState {
  return {
    version: 2,
    dataSource: 'provider',
    event: offer.event,
    attendees: [],
    ticketTypes: offer.ticketTypes,
    orders: [],
    payments: [],
    scholarships: [],
    speakers: offer.speakers.map((speaker) => ({
      ...speaker,
      email: '',
      status: speaker.status,
      pendingAssets: [],
      audiovisualRelease: false,
    })),
    sessions: offer.sessions,
    rooms: offer.rooms,
    tracks: [],
    campaigns: [],
    checkIns: [],
    sessionRegistrations: [],
    communications: [],
    coupons: [],
    waitlist: [],
    activityLogs: [],
    registrationFields: offer.registrationFields,
    products: offer.products,
    eventSponsorIds: offer.sponsors.map((sponsor) => sponsor.id),
    partners: offer.partners,
    landingPage: offer.landingPage,
    paymentConfiguration: offer.paymentConfiguration,
    integrations: [],
    contentPlan: [],
    updatedAt: offer.updatedAt,
  };
}

export function publicOfferToPreviewSponsors(offer: PublicOfferDocument): Sponsor[] {
  return offer.sponsors.map((sponsor) => ({
    ...sponsor,
    contactName: '',
    contactEmail: '',
    amount: 0,
    status: 'confirmed',
    perks: [],
  }));
}
